import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetEmailRevealUsage } from '@/react-query/podcast-queries';
import { Badge } from '@/components/ui/badge';
import { ROUTES } from '@/router/routes';
import {
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import { getSubscription, cancelSubscription, SubscriptionInfo } from '../../services/payment-service';
import { useUser } from '../../hooks/use-user';

const UsageStatsWidget = () => {
    const navigate = useNavigate();
    const { user } = useUser();
    const { data: response, isLoading: usageLoading } = useGetEmailRevealUsage();
    const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
    const [subLoading, setSubLoading] = useState(true);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [cancelling, setCancelling] = useState(false);

    useEffect(() => {
        loadSubscription();
    }, []);

    const loadSubscription = async () => {
        try {
            const data = await getSubscription();
            setSubscription(data);
        } catch (error) {
            console.error('Failed to load subscription', error);
        } finally {
            setSubLoading(false);
        }
    };

    const handleCancel = async () => {
        setCancelling(true);
        try {
            await cancelSubscription();
            setCancelDialogOpen(false);
            await loadSubscription();
        } catch (error) {
            console.error('Failed to cancel subscription', error);
        } finally {
            setCancelling(false);
        }
    };

    const isLoading = usageLoading || subLoading;

    if (isLoading || !response?.success || !response.data) {
        return (
            <div className="bg-white p-6 rounded-xl border shadow-sm">
                <div className="flex justify-center py-4">
                    <CircularProgress size={24} />
                </div>
            </div>
        );
    }

    const { tier, monthlyLimit, usedThisMonth, extraCredits } = response.data;
    const remaining = Math.max(0, monthlyLimit - usedThisMonth);
    const progress = Math.min((usedThisMonth / monthlyLimit) * 100, 100);

    const formatDate = (d: Date) => {
        const day = d.getDate().toString().padStart(2, '0');
        const month = d.toLocaleString('en-US', { month: 'short' });
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
    };

    // Calculate credit reset date from anchor, subscription billing start, or user join date
    const getResetDate = (): string => {
        const anchorSource = user?.creditResetAnchor || subscription?.billingPeriodStart || user?.createdAt;
        if (anchorSource) {
            let anchor = new Date(anchorSource);
            const now = new Date();
            // Advance anchor by calendar months until current period
            // eslint-disable-next-line no-constant-condition
            while (true) {
                const next = new Date(anchor);
                next.setMonth(next.getMonth() + 1);
                if (next > now) break;
                anchor = next;
            }
            const resetDate = new Date(anchor);
            resetDate.setMonth(resetDate.getMonth() + 1);
            return formatDate(resetDate);
        }
        const now = new Date();
        const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        return formatDate(nextMonth);
    };

    const hasPaidSubscription = subscription?.hasActiveSubscription;
    const isCancelled = subscription?.status === 'CANCELLED';

    const statusColor = subscription?.status === 'ACTIVE' ? 'success'
        : subscription?.status === 'CANCELLED' ? 'warning'
        : subscription?.status === 'ON_HOLD' ? 'error'
        : 'default';

    const statusLabel = isCancelled ? 'Active (Cancelling)' : subscription?.status;

    return (
        <>
            <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden flex flex-col">
                {/* Header: Plan + Status */}
                <div className="p-4 border-b bg-slate-50 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">Your Plan</h3>
                        {hasPaidSubscription && (
                            <Chip
                                label={statusLabel}
                                color={statusColor as any}
                                size="small"
                            />
                        )}
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Badge variant="outline" className="uppercase">{tier}</Badge>
                        {hasPaidSubscription && subscription!.billingInterval && (
                            <Badge variant="outline" className="text-xs">
                                {subscription!.billingInterval === 'ANNUAL' ? 'Annual' : 'Monthly'}
                            </Badge>
                        )}
                    </div>
                </div>

                <div className="p-5 flex-1 flex flex-col justify-center space-y-4">
                    {/* Usage Progress */}
                    <div>
                        <div className="flex justify-between items-end mb-2">
                            <span className="font-semibold text-gray-700">Email Reveals</span>
                            <span className="text-2xl font-bold text-gray-900">
                                {usedThisMonth} <span className="text-sm font-normal text-gray-400">/ {monthlyLimit}</span>
                            </span>
                        </div>
                        <div className="h-2.5 w-full bg-slate-100 rounded-full overflow-hidden">
                            <div
                                className={`h-full transition-all duration-500 ${
                                    progress > 90 ? 'bg-red-500' : progress > 75 ? 'bg-yellow-500' : 'bg-blue-600'
                                }`}
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    </div>

                    {/* Stats Row */}
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="bg-slate-50 rounded-lg p-3">
                            <span className="text-gray-500 block">Remaining</span>
                            <span className="font-semibold text-gray-900">{remaining} credits</span>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3">
                            <span className="text-gray-500 block">Resets On</span>
                            <span className="font-semibold text-gray-900">{getResetDate()}</span>
                        </div>
                    </div>

                    {/* Extra Credits */}
                    {(extraCredits || 0) > 0 && (
                        <div className="bg-purple-50 text-purple-700 px-3 py-2 rounded-lg text-sm flex justify-between items-center border border-purple-200">
                            <span>Extra Credits</span>
                            <span className="font-bold">+{extraCredits}</span>
                        </div>
                    )}

                    {/* Subscription Details */}
                    {hasPaidSubscription && (
                        <div className="border-t pt-3 space-y-1.5 text-sm">
                            {subscription!.billingPeriodEnd && !isCancelled && (
                                <div className="flex justify-between text-gray-600">
                                    <span>Next renewal</span>
                                    <span className="font-medium text-gray-900">
                                        {formatDate(new Date(subscription!.billingPeriodEnd))}
                                    </span>
                                </div>
                            )}
                            {isCancelled && subscription!.billingPeriodEnd && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
                                    <p className="text-amber-800 font-medium">Subscription cancelling</p>
                                    <p className="text-amber-700 text-xs">
                                        Your plan is still active. You'll have full access until{' '}
                                        <span className="font-semibold">
                                            {formatDate(new Date(subscription!.billingPeriodEnd))}
                                        </span>, after which you'll be moved to the Basic plan.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 pt-1">
                        <Button
                            variant="outlined"
                            size="small"
                            fullWidth
                            onClick={() => navigate(ROUTES.PRICING.path)}
                            sx={{ textTransform: 'none' }}
                        >
                            {hasPaidSubscription ? 'Change Plan' : 'Upgrade Plan'}
                        </Button>
                        {hasPaidSubscription && subscription?.status === 'ACTIVE' && (
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                fullWidth
                                onClick={() => setCancelDialogOpen(true)}
                                sx={{ textTransform: 'none' }}
                            >
                                Cancel
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Cancel Confirmation Dialog */}
            <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
                <DialogTitle>Cancel Subscription?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to cancel? You'll keep access to your{' '}
                        {subscription?.tier} plan until the end of your billing period
                        {subscription?.billingPeriodEnd
                            ? ` (${new Date(subscription.billingPeriodEnd).toLocaleDateString()})`
                            : ''
                        }. After that, you'll be downgraded to the Basic plan.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelling}>
                        Keep Subscription
                    </Button>
                    <Button onClick={handleCancel} color="error" disabled={cancelling}>
                        {cancelling ? <CircularProgress size={20} /> : 'Yes, Cancel'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default UsageStatsWidget;
